import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { moviesAPI, shortfilmsAPI, reviewsAPI, watchlistAPI } from "../utils/api";
import { getEmbedUrl } from "../utils/videoEmbed";
import { useAuth } from "../context/AuthContext";
import {
  FiStar,
  FiBookmark,
  FiPlay,
  FiFilm,
  FiClock,
  FiCalendar,
  FiGlobe,
  FiUser,
  FiTrash2,
} from "react-icons/fi";
import toast from "react-hot-toast";
import "./MovieDetail.css";

const MovieDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const isShortfilm = location.pathname.startsWith("/shortfilms");
  const contentAPI = isShortfilm ? shortfilmsAPI : moviesAPI;
  const contentName = isShortfilm ? "short film" : "movie";
  const contentTitle = isShortfilm ? "Short Film" : "Movie";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTrailer, setShowTrailer] = useState(false);
  const [showMovie, setShowMovie] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 8, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchMovie();
    fetchReviews();
    if (user) checkWatchlist();
  }, [id]);

  const fetchMovie = async () => {
    try {
      const res = await contentAPI.getById(id);
      setMovie(res.data);
    } catch (err) {
      navigate(isShortfilm ? "/shortfilms" : "/movies");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await reviewsAPI.getByMovie(id);
      setReviews(res.data);
    } catch (err) {}
  };

  const checkWatchlist = async () => {
    try {
      const res = await watchlistAPI.get();
      setInWatchlist(res.data.some((m) => m._id === id));
    } catch (err) {}
  };

  const handleWatchlist = async () => {
    if (!user) {
      toast.error("Please login");
      navigate("/login");
      return;
    }
    try {
      const res = await watchlistAPI.toggle(id);
      setInWatchlist(res.data.added);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Failed");
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to review");
      navigate("/login");
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await reviewsAPI.create(id, reviewForm);
      setReviews((prev) => [res.data, ...prev]);
      setReviewForm({ rating: 8, comment: "" });
      toast.success("Review posted!");
      fetchMovie();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to post review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await reviewsAPI.delete(reviewId);
      setReviews((prev) => prev.filter((r) => r._id !== reviewId));
      toast.success("Review deleted");
      fetchMovie();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (loading)
    return (
      <div className="loading-center" style={{ minHeight: "100vh" }}>
        <div className="spinner" />
      </div>
    );
  if (!movie) return null;

  const posterUrl = movie.poster?.startsWith("/uploads")
    ? `${import.meta.env.VITE_API_URL || "https://movbd-backend.vercel.app"}${movie.poster}`
    : movie.poster;
  const backdropUrl = movie.backdrop?.startsWith("/uploads")
    ? `${import.meta.env.VITE_API_URL || "https://movbd-backend.vercel.app"}${movie.backdrop}`
    : movie.backdrop;

  return (
    <div className="movie-detail page-enter">
      <div
        className="detail-backdrop"
        style={{ backgroundImage: `url(${backdropUrl || posterUrl})` }}
      />
      <div className="detail-overlay" />

      <div className="container detail-content">
        <div className="detail-grid">
          <div className="detail-poster">
            <img src={posterUrl} alt={movie.title} />
            <div className="poster-actions">
              {movie.movieUrl && (
                <button
                  onClick={() => {
                    if (!user) {
                      toast.error("Please login to watch");
                      navigate("/login");
                      return;
                    }
                    setShowMovie(true);
                  }}
                  className="btn btn-primary btn-lg"
                >
                  <FiFilm /> Watch {contentTitle}
                </button>
              )}
              {movie.trailerUrl && (
                <button
                  onClick={() => setShowTrailer(true)}
                  className="btn btn-outline btn-lg"
                >
                  <FiPlay /> Watch Trailer
                </button>
              )}
              <button
                onClick={handleWatchlist}
                className={`btn btn-lg ${inWatchlist ? "btn-primary" : "btn-ghost"}`}
              >
                <FiBookmark />{" "}
                {inWatchlist ? "In Watchlist" : "Add to Watchlist"}
              </button>
            </div>
          </div>

          <div className="detail-info">
            <div className="detail-badges">
              <span className="badge badge-accent">{movie.quality}</span>
              {movie.genre?.map((g) => (
                <span key={g} className="badge badge-dark">
                  {g}
                </span>
              ))}
            </div>
            <h1 className="detail-title">{movie.title}</h1>
            {movie.titleBn && (
              <p className="detail-title-bn">{movie.titleBn}</p>
            )}

            <div className="detail-stats">
              <div className="stat-box">
                <FiStar className="gold" />
                <div>
                  <strong>
                    {movie.averageRating > 0
                      ? movie.averageRating.toFixed(1)
                      : "N/A"}
                  </strong>
                  <span>{movie.totalReviews} reviews</span>
                </div>
              </div>
            </div>

            <div className="detail-meta-grid">
              {movie.releaseYear && (
                <div className="meta-item">
                  <FiCalendar />
                  <span>{movie.releaseYear}</span>
                </div>
              )}
              {movie.duration && (
                <div className="meta-item">
                  <FiClock />
                  <span>
                    {Math.floor(movie.duration / 60)}h {movie.duration % 60}m
                  </span>
                </div>
              )}
              {movie.language && (
                <div className="meta-item">
                  <FiGlobe />
                  <span>{movie.language}</span>
                </div>
              )}
              {movie.country && (
                <div className="meta-item">
                  <FiGlobe />
                  <span>{movie.country}</span>
                </div>
              )}
              {movie.director && (
                <div className="meta-item">
                  <FiUser />
                  <span>Dir: {movie.director}</span>
                </div>
              )}
            </div>

            {movie.cast?.length > 0 && (
              <div className="detail-cast">
                <h4>Cast</h4>
                <div className="cast-list">
                  {movie.cast.map((c) => (
                    <span key={c} className="cast-tag">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-desc">
              <h4>Synopsis</h4>
              <p>{movie.description}</p>
            </div>
          </div>
        </div>

        <section className="reviews-section">
          <h2 className="section-title">Reviews & Ratings</h2>
          {user && (
            <form onSubmit={handleReviewSubmit} className="review-form">
              <h3>Write a Review</h3>
              <div className="rating-input">
                <label>
                  Your Rating: <strong>{reviewForm.rating}/10</strong>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="0.5"
                  value={reviewForm.rating}
                  onChange={(e) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      rating: parseFloat(e.target.value),
                    }))
                  }
                  className="rating-slider"
                />
              </div>
              <textarea
                className="form-control"
                placeholder={`Share your thoughts about this ${contentName}...`}
                value={reviewForm.comment}
                onChange={(e) =>
                  setReviewForm((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
                rows={4}
                required
              />
              <button
                type="submit"
                disabled={submittingReview}
                className="btn btn-primary"
              >
                {submittingReview ? "Posting..." : "Post Review"}
              </button>
            </form>
          )}

          <div className="reviews-list">
            {reviews.length === 0 ? (
              <p className="no-reviews">
                No reviews yet. Be the first to review!
              </p>
            ) : (
              reviews.map((review) => (
                <div key={review._id} className="review-card">
                  <div className="review-header">
                    <div className="review-user">
                      <div className="review-avatar">
                        {review.user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <strong>{review.user?.name}</strong>
                        <span>
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="review-rating">
                      <FiStar className="gold" />
                      <strong>{review.rating}/10</strong>
                      {(user?._id === review.user?._id ||
                        user?.role === "admin") && (
                        <button
                          onClick={() => handleDeleteReview(review._id)}
                          className="delete-btn"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="review-text">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Watch Movie Modal */}
      {showMovie && (
        <div className="trailer-modal" onClick={() => setShowMovie(false)}>
          <div className="trailer-content" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowMovie(false)}
              className="close-trailer"
            >
              ✕
            </button>
            <iframe
              src={getEmbedUrl(movie.movieUrl)}
              title={contentTitle}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Trailer Modal */}
      {showTrailer && (
        <div className="trailer-modal" onClick={() => setShowTrailer(false)}>
          <div className="trailer-content" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowTrailer(false)}
              className="close-trailer"
            >
              ✕
            </button>
            <iframe
              src={getEmbedUrl(movie.trailerUrl)}
              title="Trailer"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieDetail;
